import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getPublicPageSeo } from '../../seo/publicPages';
import { buildJsonLd } from '../../seo/jsonLd';
import {
  canonicalUrl,
  isIndexableHostname,
  ogImageUrl,
} from '../../seo/siteConfig';

function currentHostname() {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}

/**
 * Per-route title, description, canonical, and social tags.
 * Marketing pages are indexable on flowdo.biz; everything else is noindex.
 */
export default function SeoHead() {
  const { pathname } = useLocation();
  const page = getPublicPageSeo(pathname);
  const onPublicHost = isIndexableHostname(currentHostname());
  const image = ogImageUrl();

  useEffect(() => {
    if (page?.title) document.title = page.title;
  }, [page]);

  if (!page) {
    return (
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  const indexable = onPublicHost && page.index !== false;
  const url = canonicalUrl(page.path);
  const jsonLd = indexable ? buildJsonLd(page) : null;

  return (
    <Helmet prioritizeSeoTags title={page.title}>
      <title>{page.title}</title>
      <meta name="description" content={page.description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={indexable ? 'index, follow' : 'noindex, nofollow'} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="flowdo" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={page.title} />
      <meta property="og:description" content={page.description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={page.title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={page.title} />
      <meta name="twitter:description" content={page.description} />
      <meta name="twitter:image" content={image} />
      {jsonLd ? <script type="application/ld+json">{jsonLd}</script> : null}
    </Helmet>
  );
}
